import { formatBloodPressureChartLabel } from './fitnessProfileBloodPressure';

function parseIsoDate(iso) {
	if (!iso) return null;
	const date = new Date(`${iso}T12:00:00`);
	return Number.isNaN(date.getTime()) ? null : date;
}

export function interpolateGoalWeightOnDate(goal, isoDate) {
	const startDate = parseIsoDate(goal.starts_on);
	const endDate = parseIsoDate(goal.target_date);
	const pointDate = parseIsoDate(isoDate);
	const startWeight = Number(goal.baseline_pounds);
	const endWeight = Number(goal.target_pounds);

	if (!startDate || !endDate || !pointDate || !Number.isFinite(startWeight) || !Number.isFinite(endWeight)) {
		return null;
	}

	const totalDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
	if (totalDays <= 0) return null;

	const dayOffset = (pointDate - startDate) / (1000 * 60 * 60 * 24);
	const value = startWeight + ((endWeight - startWeight) / totalDays) * dayOffset;
	return Math.round(value * 10) / 10;
}

/** Merge weight readings + optional goal trend into one dated chart series. */
export function buildWeightChartSeries(readingsTrend = [], weightGoal = null) {
	const dateSet = new Set();
	readingsTrend.forEach((entry) => {
		if (entry.date) dateSet.add(entry.date);
	});

	if (weightGoal?.starts_on) dateSet.add(weightGoal.starts_on);
	if (weightGoal?.target_date) dateSet.add(weightGoal.target_date);
	(weightGoal?.weight_trend || []).forEach((point) => {
		if (point.date) dateSet.add(point.date);
	});

	const sortedDates = [...dateSet].sort();
	if (!sortedDates.length) {
		return { points: [], trendLine: [] };
	}

	const readingsByDate = new Map(readingsTrend.map((entry) => [entry.date, entry]));

	const points = sortedDates.map((date) => {
		const reading = readingsByDate.get(date);
		return {
			date,
			month: formatBloodPressureChartLabel({ date }),
			value: reading && Number.isFinite(reading.value) ? reading.value : null
		};
	});

	const trendLine = weightGoal
		? sortedDates
				.map((date) => ({
					date,
					value: interpolateGoalWeightOnDate(weightGoal, date)
				}))
				.filter((entry) => Number.isFinite(entry.value))
		: [];

	return { points, trendLine };
}

export function interpolateGoalBloodPressureOnDate(goal, isoDate) {
	const startDate = parseIsoDate(goal.starts_on);
	const endDate = parseIsoDate(goal.target_date);
	const pointDate = parseIsoDate(isoDate);
	const startSys = Number(goal.baseline_systolic);
	const startDia = Number(goal.baseline_diastolic);
	const endSys = Number(goal.target_systolic);
	const endDia = Number(goal.target_diastolic);

	if (
		!startDate ||
		!endDate ||
		!pointDate ||
		!Number.isFinite(startSys) ||
		!Number.isFinite(startDia) ||
		!Number.isFinite(endSys) ||
		!Number.isFinite(endDia)
	) {
		return null;
	}

	const totalDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
	if (totalDays <= 0) return null;

	const dayOffset = (pointDate - startDate) / (1000 * 60 * 60 * 24);
	const ratio = dayOffset / totalDays;

	return {
		systolic: Math.round(startSys + (endSys - startSys) * ratio),
		diastolic: Math.round(startDia + (endDia - startDia) * ratio)
	};
}

/** Merge BP readings + optional goal trend into one dated chart series. */
export function buildBloodPressureChartSeries(readingsTrend = [], bloodPressureGoal = null) {
	const dateSet = new Set();
	readingsTrend.forEach((entry) => {
		if (entry.date) dateSet.add(entry.date);
	});

	if (bloodPressureGoal?.starts_on) dateSet.add(bloodPressureGoal.starts_on);
	if (bloodPressureGoal?.target_date) dateSet.add(bloodPressureGoal.target_date);
	(bloodPressureGoal?.blood_pressure_trend || []).forEach((point) => {
		if (point.date) dateSet.add(point.date);
	});

	const sortedDates = [...dateSet].sort();
	if (!sortedDates.length) {
		return { points: [], trendLine: [] };
	}

	const readingsByDate = new Map(readingsTrend.map((entry) => [entry.date, entry]));

	const points = sortedDates.map((date) => {
		const reading = readingsByDate.get(date);
		return {
			date,
			month: formatBloodPressureChartLabel({ date }),
			systolic: reading && Number.isFinite(reading.systolic) ? reading.systolic : null,
			diastolic: reading && Number.isFinite(reading.diastolic) ? reading.diastolic : null
		};
	});

	const trendLine = bloodPressureGoal
		? sortedDates
				.map((date) => {
					const values = interpolateGoalBloodPressureOnDate(bloodPressureGoal, date);
					if (!values) return null;
					return { date, ...values };
				})
				.filter(Boolean)
		: [];

	return { points, trendLine };
}
